import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React from 'react'

import { isEmpty } from '@livechat/data-utils'

import EmptyDataInfoBanner from './EmptyDataInfoBanner'
import { ButtonRow, StyledButton } from './sharedStyles'
import { useModal } from 'components/Modal'
import SelectField from 'components/form/SelectField'
import type { Account, Container } from 'lib/gtm'

const FormContainer = styled.div`
	display: flex;
	flex-direction: column;
`

const StyledSelect = styled(SelectField)<{ isErrorBorderVisible?: boolean }>`
	width: 100%;
	height: 40px;
	border: 0;
	background-color: #f0f0f0;
	border-radius: ${({ theme }) => theme.borderRadiuses.md};
	margin-top: ${({ theme }) => theme.spaces.space3};
	margin-bottom: ${({ theme }) => theme.spaces.space7};

	${({ isErrorBorderVisible, theme }) =>
		isErrorBorderVisible &&
		css`
			border: 1px solid ${theme.colors.light.toast.error.background};
		`}
`

const AlreadyInstalledWarning = styled.p<{ isVisible: boolean }>`
	color: ${({ theme }) => theme.colors.light.toast.error.background};
	font-size: 12px;
	margin-top: -20px;
	opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
	transition: opacity 0.2s ease-in-out;
`

const AlreadyInstalledWarningOuter = styled.div`
	display: flex;
	flex-direction: column;
`

type Props = {
	accounts: Account[]
	containers: Container[] | null
	selectedAccountId: string | null
	selectedContainerId: string | null
	isSaving: boolean
	hasDirtyWorkspace: boolean
	hasAlreadyInstalledOW: boolean
	onChangeAccount: (accountId: string) => void
	onChangeContainer: (containerId: string) => void
	handleAddToWorkspace: () => void
	handlePublish: () => void
}

const GTMConfigurationForm = ({
	accounts,
	containers,
	handleAddToWorkspace,
	handlePublish,
	hasAlreadyInstalledOW,
	hasDirtyWorkspace,
	isSaving,
	onChangeAccount,
	onChangeContainer,
	selectedAccountId,
	selectedContainerId,
}: Props) => {
	const parsedAccountData = React.useMemo(
		() =>
			accounts.map((item) => ({
				value: item.accountId,
				label: item.name,
			})),
		[accounts],
	)

	const parsedContainerData = React.useMemo(() => {
		if (!containers) {
			return []
		}

		return containers.map((item) => ({
			value: item.containerId,
			label: item.name,
		}))
	}, [containers])

	const { hideModal } = useModal()

	return (
		<FormContainer>
			<StyledSelect
				label="GTM Account"
				name="GTM Account"
				onChange={onChangeAccount}
				options={parsedAccountData}
				value={selectedAccountId ?? ''}
			/>
			{containers && isEmpty(containers) ? (
				<EmptyDataInfoBanner displayType="container" />
			) : (
				<>
					<AlreadyInstalledWarningOuter>
						<StyledSelect
							label="Container"
							name="Container"
							onChange={onChangeContainer}
							options={parsedContainerData}
							value={selectedContainerId ?? ''}
							isErrorBorderVisible={hasAlreadyInstalledOW}
						/>
						<AlreadyInstalledWarning aria-hidden={!hasAlreadyInstalledOW} isVisible={hasAlreadyInstalledOW}>
							OpenWidget is already added to this container. Choose different container to add it.
						</AlreadyInstalledWarning>
					</AlreadyInstalledWarningOuter>
					<ButtonRow>
						<StyledButton kind="secondary" onClick={hideModal}>
							Cancel
						</StyledButton>
						{hasDirtyWorkspace && !hasAlreadyInstalledOW ? (
							<StyledButton kind="primary" onClick={handleAddToWorkspace} loading={isSaving} disabled={isSaving}>
								Add to workspace
							</StyledButton>
						) : (
							<StyledButton
								kind="primary"
								disabled={isSaving || hasAlreadyInstalledOW}
								onClick={handlePublish}
								loading={isSaving}
							>
								Add and publish
							</StyledButton>
						)}
					</ButtonRow>
				</>
			)}
		</FormContainer>
	)
}

export default GTMConfigurationForm
